import { useState } from 'react';
import { Box, Flex, Group, Stack, Text } from '@mantine/core';
import { useNavigate, useParams } from 'react-router';
import TextInput from 'src/components/design-system/input/TextInput';
import Textarea from 'src/components/design-system/input/Textarea';
import { Button } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { useFamilyMeetingParticipants } from 'src/meetings/hooks/useFamilyMeetingParticipants';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ChooseClientModal } from './ChooseClientModal';

const DESCRIPTION_MAX_LENGTH = 1000;

export default function AddMeetingAttendeePage() {
  const { familyId = '', eventId = '' } = useParams<{ familyId: string; eventId: string }>();
  const navigate = useNavigate();
  const { addParticipant } = useFamilyMeetingParticipants(familyId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chooseClientOpen, setChooseClientOpen] = useState(false);

  const meetingsPath = `/family/${familyId}/meetings`;
  const canAdd = name.trim().length > 0 && description.trim().length > 0;

  async function handleAdd() {
    try {
      await addParticipant.mutateAsync({
        eventId,
        body: {
          participantType: 'EXTERNAL',
          name: name.trim(),
          description: description.trim(),
        },
      });
      navigate(meetingsPath);
    } catch (error) {
      notifyAndLogError(
        'Failed to add attendee',
        'Could not add the attendee to this meeting. Please try again.',
        error,
      );
    }
  }

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <Box px="xl" pt="xxl">
            <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
              Add new attendee
            </Text>
          </Box>
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={() => navigate(meetingsPath)}
                onAction={handleAdd}
                actionLabel="Add attendee"
                actionDisabled={!canAdd || addParticipant.isPending}
                actionLoading={addParticipant.isPending}
              />
            }
          >
            <Stack gap={0} px="xxs" pt="xl">
              <Group gap="xxs" align="center">
                <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Enter attendee name or
                </Text>
                <Button
                  variant="primary"
                  size="sm"
                  style={{ borderRadius: 10 }}
                  onClick={() => setChooseClientOpen(true)}
                >
                  Choose a client
                </Button>
              </Group>
              <Box mt="md">
                <TextInput
                  size="lg"
                  placeholder="Enter attendee name"
                  required
                  value={name}
                  onChange={event => setName(event.currentTarget.value)}
                  styles={{ input: { border: '1px solid var(--mantine-color-gray-3)' } }}
                />
              </Box>
              <Box mt="xxs" style={{ position: 'relative' }}>
                <Textarea
                  size="lg"
                  placeholder={
                    'Describe who this is - e.g. "David\'s wife" or "solicitor from Thornton & Co"'
                  }
                  required
                  autosize
                  minRows={3}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  value={description}
                  onChange={event => setDescription(event.currentTarget.value)}
                  styles={{
                    input: { border: '1px solid var(--mantine-color-gray-3)', paddingBottom: 28 },
                  }}
                />
                <Flex gap={0} style={{ position: 'absolute', right: 16, bottom: 12 }}>
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                    {description.length}
                  </Text>
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.4">
                    /{DESCRIPTION_MAX_LENGTH}
                  </Text>
                </Flex>
              </Box>
            </Stack>
          </FlowBody>
        }
      />
      <ChooseClientModal
        opened={chooseClientOpen}
        onClose={() => setChooseClientOpen(false)}
        familyId={familyId}
        eventId={eventId}
      />
    </Flex>
  );
}
